import React, { useEffect, useLayoutEffect, useRef, useState, FC } from 'react';
import {
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Box,
  Typography,
  useTheme,
  SelectChangeEvent,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import clsx from 'clsx';

interface Option {
  label: string;
  value: string;
}

interface SelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  id: string;
  error?: boolean;
  helperText?: string;
  options?: Option[];
  disabled?: boolean;
  sx?: object;
  MenuProps?: MuiSelectProps['MenuProps'];
}

const SelectTransparent: FC<SelectProps> = ({
  label,
  value: propValue,
  onChange,
  id,
  error = false,
  helperText,
  options = [{ label: '1', value: '1' }],
  disabled = false,
  sx = {},
  MenuProps = {},
}) => {
  const theme = useTheme();

  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [value, setValue] = useState(propValue || '');
  const [isCustomValue, setIsCustomValue] = useState(false);
  const prevValueRef = useRef(propValue);

  useEffect(() => {
    setIsOptionSelected(value !== '');
  }, [value]);

  useLayoutEffect(() => {
    if (propValue !== prevValueRef.current) {
      setValue(propValue);
      prevValueRef.current = propValue;
    }
  }, [propValue]);

  useEffect(() => {
    setIsCustomValue(!options.some((option) => option.value === value));
  }, [value, options]);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    event.stopPropagation();
    const newValue = event.target.value;
    onChange(newValue);
    setIsOptionSelected(newValue !== '');
  };

  const mergedMenuProps = {
    ...MenuProps,
    sx: {
      '.MuiMenu-paper': {
        marginTop: '6px',
        borderRadius: '12px',
        px: '8px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        '& .MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
      ...MenuProps.sx,
    },
  };

  return (
    <FormControl
      variant="filled"
      disabled={disabled}
      sx={{
        width: '100%',
        position: 'relative',
        ...sx,
        '.MuiFilledInput-root': {
          backgroundColor: 'transparent',
          borderRadius: 4,
          borderColor: `${error ? '#E53E3E' : 'black'}`,
          border: `1px solid ${error ? '#E53E3E' : theme.palette.divider}`,
          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: `${error ? '#E53E3E' : 'black'}`,
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
            borderColor: `${error ? '#E53E3E' : 'black'}`,
          },
          '&:before, &:after': {
            content: 'none',
          },
        },
        '.MuiInputLabel-root': {
          color: `${error ? '#E53E3E' : theme.palette.text.secondary}`,
          position: 'absolute',
          left: 12,
          top: error ? '46.5%' : '70%',
          transform: isOptionSelected
            ? 'translateY(-140%) scale(0.75)'
            : 'translateY(-100%)',
          transition: theme.transitions.create(['top', 'transform'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&.Mui-focused': {
            color: `${error ? '#E53E3E' : theme.palette.text.secondary}`,
          },
        },
      }}
    >
      <InputLabel
        shrink={isOptionSelected}
        htmlFor={id}
        sx={{
          fontSize: '16px',
          fontWeight: '700',
          fontFamily: 'AlmoniDL',
          textAlign: 'right',
        }}
      >
        {label}
      </InputLabel>
      <MuiSelect
        labelId={`${id}-label`}
        id={id}
        value={value}
        onChange={handleSelectChange}
        displayEmpty
        notched={false}
        error={error}
        className={clsx({ 'option-selected': isOptionSelected })}
        MenuProps={mergedMenuProps}
        sx={{
          '.MuiSelect-select': {
            '&:focus': {
              backgroundColor: 'transparent',
            },
          },
          '&:before, &:after': {
            borderBottom: 'none',
          },
          '.MuiSelect-icon': {
            transition: theme.transitions.create(['top'], {
              duration: theme.transitions.duration.shorter,
            }),
          },
          '&.option-selected .MuiSelect-icon': {
            top: 'calc(50% - 3px)',
          },
        }}
      >
        {isCustomValue && (
          <MenuItem value={value} disabled>
            {value}
          </MenuItem>
        )}
        {options.map((option, index) => (
          <MenuItem
            sx={{ borderRadius: '8px' }}
            key={index}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
          <ErrorOutlineIcon
            sx={{ color: '#E53E3E', mr: '5px', height: '14px', width: '14px' }}
          />
          <Typography
            variant="body1"
            fontWeight="fontWeightRegular"
            fontSize={14}
            sx={{ color: '#E53E3E' }}
          >
            {helperText}
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};

export default SelectTransparent;
